import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiValidationError } from '@/lib/api';
import type { Message, MessageInput, ListMessagesResponse } from 'shared';

export type { Message, MessageInput };

// ─── Query params ─────────────────────────────────────────────────────────────

export interface UseMessagesParams {
  limit?: number;
  offset?: number;
  search?: string;
  activeOnly?: boolean;
  showDeleted?: boolean;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Paginated list of messages */
export function useMessages(params: UseMessagesParams = {}) {
  const {
    limit = 20,
    offset = 0,
    search = '',
    activeOnly = false,
    showDeleted = false,
  } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(search ? { search } : {}),
    ...(activeOnly ? { activeOnly: 'true' } : {}),
    ...(showDeleted ? { showDeleted: 'true' } : {}),
  });

  return useQuery({
    queryKey: ['messages', limit, offset, search, activeOnly, showDeleted],
    queryFn: () => api.get<ListMessagesResponse>(`/api/messages?${qs}`),
    staleTime: 1000 * 30,
  });
}

/** Create a new message */
export function useCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MessageInput) =>
      api.post<Message>('/api/messages', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (err: unknown) => {
      // Re-throw so callers can inspect ApiValidationError
      if (err instanceof ApiValidationError) throw err;
      throw err;
    },
  });
}

/** Update an existing message */
export function useUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MessageInput }) =>
      api.put<Message>(`/api/messages/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiValidationError) throw err;
      throw err;
    },
  });
}

/** Soft-delete a message (requires reason) */
export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.delete(`/api/messages/${id}`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiValidationError) throw err;
      throw err;
    },
  });
}
